import { EventEmitter } from 'events';
import fields from './fields';
export const emitter = new EventEmitter();

/**
 * An emptied TYPED column goes on the wire as null, never as "".
 *
 * `formatFromInput` turns null into "" on the way INTO an input, which is right
 * for display — an input's value must be a string. Nothing turned it back on
 * the way out, so clearing a date or a number sent `""` to the socket, and
 * MySQL in strict mode refuses that outright for a DATE or an INT:
 *
 *     Incorrect date value: '' for column 'WarehouseDate' at row 1
 *
 * which the user saw verbatim. So clearing an optional date or number did not
 * merely look wrong, it did not work at all.
 *
 * Here rather than in `formatFromInput`, which has many callers and is used in
 * both directions, and rather than in each modal's handleBlur: this is the one
 * place every inline edit crosses to the wire. Strings keep "" — an empty text
 * column and a NULL one read the same, and swapping them would churn the audit
 * trail on every blanked note.
 *
 * The server coerces too, because a stale tab still sends the old shape.
 */
const nullIfEmptyTyped = (msg) => {
    const meta = fields[msg.fieldName];
    if (!meta || meta.dataType === "string") return msg;
    if (msg.fieldValue !== "" && msg.fieldValue !== undefined) return msg;
    return { ...msg, fieldValue: null };
};

const UtilityFunctions = {
  formatFromInput: function(value, fieldType){
    if (value === null || value === undefined) return "";

    switch (fieldType) {
      case "integer":
        return typeof value === "string" ? Number(value.replace(/,/g, '')) : value;
      case "decimal":
        return typeof value === "string" ? Number(value.replace(/,/g, '')) : value;
      case "date":
        return typeof value === "string" ? value.split("T")[0] : value;
      case "datetime":
        if (value === null || value === undefined) return "";
        return typeof value === "string" ? value : (value instanceof Date ? value.toISOString() : String(value));
      case "boolean":
        return value === 1 || value === "1" || value === true;
      default:
        if (typeof value === 'object') return value.message != null ? String(value.message) : String(value);
        return value;
    }
  },
  formatIntoInput: function(value, fieldType){
    //Formats data before it's displayed in input fields
    //For numbers, it adds commas
    switch(fieldType){
      case "decimal":
          //Blank (null/undefined/"") renders blank (not "0") so genuinely-empty
          //numerics — e.g. a NULL PaymentPerformanceScore, normalized to "" at
          //ingress — don't read as a real zero.
          if(value===null || value===undefined || value==="") return "";
          return new Intl.NumberFormat().format(value);
      case "date":
        //If the value is null or undefined, return an empty string
        if(value===null || value===undefined) return "";
        return String(value).split("T")[0];
      case "datetime": {
        if(value===null || value===undefined) return "";
        const dt = new Date(value);
        if(isNaN(dt.getTime())) return String(value);
        return dt.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
      }
      case "integer":
          if(value===null || value===undefined || value==="") return "";
          return new Intl.NumberFormat().format(value);
      default:
          if (value == null) return "";
          if (typeof value === 'object') return value.message != null ? String(value.message) : String(value);
          return value;
    }
  },
  getDaysDifference: function(date1, date2) {
    // Convert the input dates to Date objects if they are not already
    date1 = new Date(date1);
    date2 = new Date(date2);

    // Calculate the difference in milliseconds
    let differenceInMilliseconds = date2 - date1;

    // Convert milliseconds to days and return the result
    return Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
  },
  sortArray: (array, field, order)=>{
    //Copy first: sorting the state array in place returns the same reference,
    //which React treats as "no change"
    return [...array].sort((a, b) => {
        // Compare the field values
        const valueA = a[field];
        const valueB = b[field];

        // NULL/undefined/"" all sort last, regardless of direction (e.g. a
        // "Not started" PaymentPerformanceScore stays at the bottom either way;
        // "" is the post-edit normalized form of a NULL numeric field).
        const aNull = valueA === null || valueA === undefined || valueA === "";
        const bNull = valueB === null || valueB === undefined || valueB === "";
        if (aNull && bNull) return 0;
        if (aNull) return 1;
        if (bNull) return -1;

        // Sort in ascending order
        if (order === 'asc') {
            if (valueA < valueB) return -1;
            if (valueA > valueB) return 1;
            return 0;
        }
        // Sort in descending order
        else if (order === 'desc') {
            if (valueA < valueB) return 1;
            if (valueA > valueB) return -1;
            return 0;
        }
        return 0;
    });
  },
  /**
   * Send a single field edit over the socket, or fail loudly.
   *
   * This replaced sendOrQueue(), which held undelivered edits in a queue owned
   * by the calling modal. That queue lied. When the socket was merely reconnecting
   * it kept the new value on screen and pushed the message into a `useRef` local
   * to the modal — so closing the modal or refreshing the page dropped the edit
   * silently, after the user had watched it appear to save. Past five queued
   * messages it dropped them outright, still without reverting the display.
   *
   * On a system built for several people to look at the same sheet, an edit that
   * one person can see and nobody else ever will is the worst available outcome:
   * it is not a lost edit, it is two people holding different beliefs about the
   * same row. So there is no queue any more. If the socket is not connected the
   * field reverts to its last known-saved value and the user is told.
   *
   * The cost is real and accepted: a momentary blip that the queue used to paper
   * over now surfaces as a failed edit the user must redo. Socket.IO reconnects
   * in seconds, and never lying about a save is worth more than that.
   */
  sendOrFail: (msg, sock, data, backup, setter)=>{
    //Before the spreads below, which copy fieldValue into eventArr as well —
    //coercing afterwards would leave the optimistic UI holding "" while the
    //database held NULL.
    msg = nullIfEmptyTyped(msg);
    if(sock.connected) {
      //Check msg.triggerRefresh
      if(msg.triggerRefresh){ //Send "update_record" and "replace_record"
        sock.emit('updatefield', {
          ...msg,
          eventArr: [
            {
              eventName: 'update_record', //Of subSheet
              eventProperties: {
                ownSheetName: msg.ownSheetName, //Self
                primaryKeyName: msg.primaryKeyName,
                primaryKeyValue: msg.primaryKeyValue,
                fieldName: msg.fieldName,
                fieldValue: msg.fieldValue,
              }
            },
            {
              eventName: 'replace_record', //Of main sheet
              eventProperties: { //If it has a parent, replace the parent's record. Else, replace "own" i.e immediate sheet.
                ownSheetName: msg.parentSheetName?msg.parentSheetName:msg.ownSheetName, //Parent
                primaryKeyName: msg.parentPKeyName?msg.parentPKeyName:msg.primaryKeyName,
                primaryKeyValue: msg.parentPKeyValue?msg.parentPKeyValue:msg.primaryKeyValue
              }
            }
          ]
        });
      } else { //Send only "update_record"
        sock.emit('updatefield', {
          ...msg,
          eventArr: [
            {
              eventName: 'update_record', //Of subsheet
              eventProperties: {
                ownSheetName: msg.ownSheetName,
                primaryKeyName: msg.primaryKeyName,
                primaryKeyValue: msg.primaryKeyValue,
                fieldName: msg.fieldName,
                fieldValue: msg.fieldValue
              }
            },
            (()=>{
              if(msg.parentSheetName){
                return {
                  eventName: 'update_record', //Of main sheet
                  eventProperties: {
                    ownSheetName: msg.parentSheetName,
                    primaryKeyName: msg.parentPKeyName,
                    primaryKeyValue: msg.parentPKeyValue,
                    fieldName: msg.fieldName,
                    fieldValue: msg.fieldValue
                  }
                }
              }
            })() //If it has a parent, send parent event too
          ]
        });
      }
    } else {
      //Not connected — including sock.active, i.e. "reconnecting". That case used
      //to be treated as good enough to keep the value on screen, which is exactly
      //the branch that lied. Reverting and telling the user is now the only path.
      setter({...data, [msg.fieldName]: backup[msg.fieldName]});
      //Cars.jsx (the app shell, so this is global) owns the notice and stays
      //quiet while the tab is hidden — phones routinely kill the socket on
      //backgrounding and that is not worth alarming anyone about.
      emitter.emit("server-disconnected", { fieldName: msg.fieldName });
    }
  },
}
const passesFilter = (value, filterObj, datatype) => {
  if (!filterObj || !filterObj.filterType) return true; // No filter, always passes

  const { filterType, lowerValue, upperValue } = filterObj;

  if (datatype === "decimal" || datatype === "integer" || datatype === "date" || datatype === "datetime") {
      if (datatype === "date" || datatype === "datetime"){
        value = new Date(value).getTime(); // Convert date to timestamp
      } else { //The value could come as a string
        value = Number.parseFloat(value);
      }

      const isDateType = datatype === "date" || datatype === "datetime";
      const filterVal1 = isDateType ? new Date(lowerValue).getTime() : parseFloat(lowerValue);
      const filterVal2 = upperValue ? (isDateType ? new Date(upperValue).getTime() : parseFloat(upperValue)) : null;
      //console.log(`Value: ${value}, filterType: ${filterType} filterVal1: ${lowerValue}, filterVal2: ${upperValue}`);
      switch (filterType) {
          case '>': return value > filterVal1;
          case '>=': return value >= filterVal1;
          case '<': return value < filterVal1;
          case '<=': return value <= filterVal1;
          case '=': return value === filterVal1;
          case '!=': return value !== filterVal1;
          case 'between': return filterVal2 !== null ? (value >= filterVal1 && value <= filterVal2) : false;
          default: return true; // Unknown filter type, let it pass
      }
  } else if (datatype === "string") {
      // Check if the value is a string
      if (typeof value !== "string") return true;
    
      return value.toLowerCase().includes(lowerValue.toLowerCase()); // Case-insensitive match
  }

  return false; // If the datatype doesn't match, return false
};

function generateBase64String() {
    // Generate 12 random bytes
    const randomBytes = crypto.getRandomValues(new Uint8Array(12));
    
    // Convert to Base64
    let base64String = btoa(String.fromCharCode(...randomBytes));
    
    // Convert to URL-safe Base64 (filesystem-safe)
    base64String = base64String
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, ''); // Remove padding
    
    return base64String;
}

async function generateKey(salt) {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
      "raw",
      encoder.encode(salt),
      { name: "PBKDF2" },
      false,
      ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
      {
          name: "PBKDF2",
          salt: encoder.encode(salt), // Salting for extra security
          iterations: 100000, // Recommended for PBKDF2
          hash: "SHA-256"
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
  );
}

async function encrypt(text, salt) {
  const key = await generateKey(salt);
  const iv = crypto.getRandomValues(new Uint8Array(12)); // Generate a random IV
  const encoder = new TextEncoder();
  const encryptedData = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      encoder.encode(text)
  );

  return `${btoa(String.fromCharCode(...new Uint8Array(encryptedData)))}:${btoa(String.fromCharCode(...iv))}`
}

async function decrypt(encryptedText, salt) {
  if(!encryptedText) return "";
  const [ encryptedBase64, ivBase64 ] = encryptedText.split(":");
  const key = await generateKey(salt);
  const iv = new Uint8Array(atob(ivBase64).split("").map(c => c.charCodeAt(0)));
  const encryptedData = new Uint8Array(atob(encryptedBase64).split("").map(c => c.charCodeAt(0)));

  const decryptedBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      encryptedData
  );

  return new TextDecoder().decode(decryptedBuffer);
}

//Removed: generatePassword().
//
//It minted the temporary password for a new user, which the browser then POSTed
//in plaintext to /adduseraccount to be SMSed onward. New users now receive an
//emailed link and choose their own password, so no credential is generated
//client-side any more — see node/helpers/userInvite.js. Nothing should bring
//this back: a password the client invents is a password the client has to
//transmit.

const filterDataUtility = (setFilterObject, fieldName, filterType, lowerValue, upperValue)=>{
  console.log(fieldName, filterType, lowerValue, upperValue);
  //if(!filterType) return {}; //This way, just call with only first argument to clear the filter
  setFilterObject(prev=>{
      if(fieldName==="") return {}; //If fieldName is empty, clear all filters
      return {...prev, [fieldName]: filterType ? { filterType, lowerValue, upperValue } : {}}
  });
}

const formatPhoneForDisplay = (phone) => {
  if (phone && phone.startsWith('256')) {
    return '0' + phone.substring(3);
  }
  return phone || '';
};

const formatPhoneForStorage = (phone) => {
  if (phone && phone.startsWith('0')) {
    return '256' + phone.substring(1);
  }
  return phone || '';
};

//Fields that hold phone numbers: state keeps them display-formatted ('07…'),
//matching the fields.js validation regexes; formatPhoneForStorage converts at the send boundary
const PHONE_DISPLAY_FIELDS = ["Phone1", "Phone2", "ContactPhone1", "ContactPhone2", "CompanyPhone1", "CompanyPhone2"];

//Normalizes one incoming field to the types the sheets store:
//Numbers for integer/decimal (mysql2 sends DECIMAL as strings), date-only
//date strings, booleans, display-formatted phones
const normalizeValue = (fieldName, value, fieldsDefinition) => {
  let v = fieldsDefinition[fieldName]
    ? UtilityFunctions.formatFromInput(value, fieldsDefinition[fieldName].dataType)
    : value;
  if (PHONE_DISPLAY_FIELDS.includes(fieldName)) {
    v = formatPhoneForDisplay(v);
  }
  return v;
};

//Must be applied at EVERY ingress into the sheet state arrays — HTTP fetches
//and the add/replace/update socket events — so a live-updated row can never
//hold different types than a freshly fetched one
const normalizeRecords = (records, fieldsDefinition) => {
  return records.map((rowObj) => {
    const newRowObj = { ...rowObj };
    Object.keys(newRowObj).forEach((field) => {
      newRowObj[field] = normalizeValue(field, newRowObj[field], fieldsDefinition);
    });
    return newRowObj;
  });
};

//The one currency formatter. Everything money-shaped in the app goes through here.
//
//There were eight, in four dialects, all rendering the same 1,500,000 differently:
//"UGX 1,500,000" (Intl en-US), "USh 1,500,000" (the same call with a different
//locale — style:'currency' resolves the symbol from the locale), "1,500,000 UGX"
//(toLocaleString plus a suffix) and "UGX 1.5M". Showing a dealership two different
//symbols for their own money on two screens is a trust problem, not a style one.
//
//"UGX" prefixed wins: already the majority, already what the abbreviating report
//formatter used, and it cannot be misread the way "USh" can. Chart axes and PDF
//columns that carry the unit in the header pass `symbol: false`.
//
//  formatUGX(1500000)                     -> "UGX 1,500,000"
//  formatUGX(1500000, {abbreviate:true})  -> "UGX 1.5M"
//  formatUGX(1500000, {symbol:false})     -> "1,500,000"
//  formatUGX(1500, {currency:'USD'})      -> "USD 1,500"
const formatUGX = (value, { abbreviate = false, symbol = true, currency = 'UGX' } = {}) => {
  const n = Number(value);
  //Blank rather than "UGX 0" for genuinely-absent values, matching how
  //formatIntoInput treats a NULL numeric — a missing figure is not a zero one.
  if (value === null || value === undefined || value === '' || !Number.isFinite(n)) return '';

  const prefix = symbol ? `${currency} ` : '';
  const abs = Math.abs(n);

  if (abbreviate && abs >= 1_000) {
    const [divisor, suffix] = abs >= 1_000_000_000 ? [1_000_000_000, 'B']
      : abs >= 1_000_000 ? [1_000_000, 'M']
      : [1_000, 'K'];
    return `${prefix}${(n / divisor).toFixed(1)}${suffix}`;
  }

  //en-US for digit grouping only. Deliberately NOT style:'currency' — that is
  //what resolves the symbol from the locale, and is how "USh" got in.
  return `${prefix}${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)}`;
};

//The date the USER picked, as YYYY-MM-DD.
//
//NOT `d.toISOString().split('T')[0]`, which is what every date range in this app
//used to send. A DatePicker hands back local midnight, and toISOString() renders
//that instant in UTC — which anywhere east of UTC is 21:00 the day BEFORE. Every
//range the app requested therefore started a day early: pick 1 September, get
//data from 31 August.
//
//Invisible on most screens, structural on a monthly one. The expenditures report
//builds its window from month starts, so the shift pushed the start into the
//previous month and MySQL's DATE_FORMAT(?, '%Y-%m-01') rounded it down to that
//month's first — the user asked for six months and the chart drew seven, the
//extra one carrying a full month of tracker rental beside a single day of
//everything else.
//
//The repo already spells this out by hand in useTabJoyride, TestimonialModal,
//exportExcel, validatePreview and harness; creditCollection.js has the YYYY-MM
//sibling (monthKey). This is the missing member of that family.
const toLocalYMD = (d) => (d
  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  : null);

//A date as a person writes it — "1 November 2026" — from either a bare
//`YYYY-MM-DD` or a full timestamp. The other direction from toLocalYMD above,
//and the same trap, which is why the two live together.
//
//The date-only branch is not decoration. `new Date('2026-11-01')` is UTC
//midnight by spec, so this renders "31 October 2026" for anyone west of UTC and
//"1 November" for us purely by luck of being three hours east. The billing
//screens quote a date the server took trouble to send as a plain one — see
//localYMD in node/billingService.js, which refuses to parse it for the same
//reason — and a subscription said to start a day early is a support call.
//
//A timestamp still goes through Date on purpose: `...T21:00:00Z` names an
//instant, and which local day it lands on is a real question. A bare date is
//already the answer to that question.
//
//This replaced two different private `day()` helpers, in CreditsSettings.jsx and
//InvoiceDialog.jsx — a file and the dialog it renders — which took the two
//branches above and each handled only one.
const formatLongDate = (value) => {
  if (!value) return '—';
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

//Read a JSON draft out of localStorage without ever throwing.
//
//Drafts are parsed inside useState initializers, which run on the render path —
//an exception there escapes to the ErrorBoundary, and because the boundary
//re-renders the same initializer the user is pinned on the fallback screen and
//cannot reopen the form until they clear site data by hand. A draft is a
//convenience, never worth a lockout, so a corrupt one is discarded: we drop the
//key so the next open starts clean and return null for the caller to fall back on.
const readJsonDraft = (key) => {
  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;   //private-mode / disabled storage — behave as "no draft"
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    //A draft is always an object. `null`, a bare number or an array all parse
    //cleanly but would spread into garbage downstream, so treat them as corrupt.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('draft is not an object');
    }
    return parsed;
  } catch (err) {
    console.warn(`Discarding unreadable draft "${key}":`, err.message);
    try { localStorage.removeItem(key); } catch { /* nothing more we can do */ }
    return null;
  }
};

export default UtilityFunctions;
export { formatPhoneForDisplay, formatPhoneForStorage, PHONE_DISPLAY_FIELDS, normalizeValue, normalizeRecords, generateBase64String, passesFilter, encrypt, decrypt, filterDataUtility, readJsonDraft, formatUGX, toLocalYMD, formatLongDate };